import {
  AutocompleteInputChangeReason,
  Box,
  Chip,
  InputAdornment,
  Typography,
  autocompleteClasses,
} from "@mui/material";
import { inputAdornmentClasses } from "@mui/material/InputAdornment";
import { inputBaseClasses } from "@mui/material/InputBase";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import type { HTMLAttributes } from "react";
import React, { memo, useEffect, useState } from "react";
import { AutocompleteField } from "src/shared/components";
import type { IAutocompleteOption } from "src/shared/types/field.types";
import type { IFilterItem } from "../../model/types";
import { EFilterType } from "../../model/types";
import { FilterButton } from "./FilterButton";

interface IAgency {
  name?: string;
  value?: number;
}

interface IChipLabeledOption<T = number> extends IAutocompleteOption<T> {
  agency?: IAgency;
  label?: string;
}

type BaseAutocompleteFilter<TValue, T> = Omit<
  IFilterItem,
  "options" | "value" | "type"
> & {
  type: EFilterType.SINGLE_AUTOCOMPLETE | EFilterType.MULTI_AUTOCOMPLETE;
  value: TValue;
  options?: IChipLabeledOption<T>[];
  onInputChange?: (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
};

type PropsSingle<T> = {
  multiple?: false;
  filter: BaseAutocompleteFilter<T | null, T> & {
    type: EFilterType.SINGLE_AUTOCOMPLETE;
  };
  onChange: (v: { name: string; value: T | null }) => void;
};

type PropsMulti<T> = {
  multiple: true;
  filter: BaseAutocompleteFilter<T[], T> & {
    type: EFilterType.MULTI_AUTOCOMPLETE;
  };
  onChange: (v: { name: string; value: T[] }) => void;
};

type TableFilterAutocompleteProps<T> = PropsSingle<T> | PropsMulti<T>;

function isMultiValue<T>(value: T | T[] | null): value is T[] {
  return Array.isArray(value);
}

function TableFilterAutocomplete<T>(props: TableFilterAutocompleteProps<T>) {
  const {
    filter: {
      options,
      name,
      value,
      title,
      hideClearButton,
      onInputChange,
      loading,
      placeholder,
    },
    onChange,
    multiple,
  } = props;

  const incomingOptions = options ?? [];

  let showClearButton = Boolean(value);
  if (isMultiValue(value)) showClearButton = value.length > 0;
  if (hideClearButton) showClearButton = false;

  const selectedOption =
    !multiple && value != null
      ? incomingOptions.find((o) => o.value === value) ?? null
      : null;

  const getOptionLabel = (opt: string | IChipLabeledOption<T>): string => {
    if (typeof opt === "string") return opt;
    if (opt.name) return opt.name;
    if (opt.label) return opt.label;

    return String(opt.value);
  };

  const isOptionEqualToValue = (
    a: string | IChipLabeledOption<T>,
    b: string | IChipLabeledOption<T>,
  ): boolean => {
    if (typeof a === "string" || typeof b === "string") return a === b;

    return a.value === b.value;
  };

  // Needs refactoring to lift props to the parent component.
  const AgencyChip = ({ agency }: { agency?: IAgency }) => {
    if (!agency?.name) return null;

    const borderColorMap: Record<number, object> = {
      3: { borderColor: "secondary.main" },
      0: { borderColor: "primary.main" },
    };

    const baseChipStyles = {
      color: "#000",
      borderWidth: "2px",
      fontWeight: 500,
    };

    const getColor = (val?: number) => ({
      ...baseChipStyles,
      ...(val !== undefined
        ? borderColorMap[val]
        : { borderColor: "grey.400" }),
    });

    return (
      <Chip
        size="small"
        variant="outlined"
        label={agency.name}
        sx={{ ml: 0.5, ...getColor(agency.value) }}
      />
    );
  };

  const dynamicSuffix =
    !multiple && selectedOption ? (
      <AgencyChip agency={selectedOption.agency} />
    ) : null;

  const [cachedOptions, setCachedOptions] =
    useState<IChipLabeledOption<T>[]>(incomingOptions);

  useEffect(() => {
    const next = options ?? [];

    setCachedOptions((prev) => {
      const nextMap = new Map<string, IChipLabeledOption<T>>();
      const prevMap = new Map<string, IChipLabeledOption<T>>();

      next.forEach((option) => {
        nextMap.set(String(option.value), option);
      });

      prev.forEach((option) => {
        prevMap.set(String(option.value), option);
      });

      const selectedValues = isMultiValue(value)
        ? value
        : value != null
          ? [value]
          : [];

      selectedValues.forEach((selectedValue) => {
        const key = String(selectedValue);

        if (nextMap.has(key)) return;

        const optionFromPrev = prevMap.get(key);

        if (optionFromPrev) {
          nextMap.set(key, optionFromPrev);
        }
      });

      return Array.from(nextMap.values());
    });
  }, [options, value]);

  if (!options) return <Typography>Нет опций</Typography>;

  return (
    <AutocompleteField<T, typeof multiple>
      loading={loading}
      disableCloseOnSelect={multiple}
      placeholder={placeholder}
      multiple={multiple}
      className={
        (isMultiValue(value) ? value.length > 0 : !!value) ? "filled" : ""
      }
      name={name}
      defaultValue=""
      startAdornment={
        <InputAdornment sx={{ m: 0 }} position="start">
          {title}
          <Typography
            component="span"
            className="separator"
            sx={{ display: "none" }}
          >
            :
          </Typography>
        </InputAdornment>
      }
      onInputChange={(e, input, reason) => onInputChange?.(e, input, reason)}
      value={value}
      onChange={(v) => {
        if (multiple) {
          onChange({ name, value: v as T[] });
        } else {
          onChange({ name, value: v as T | null });
        }
      }}
      renderTags={(v) => String(v.length)}
      endAdornment={
        dynamicSuffix || showClearButton ? (
          <InputAdornment position="end" sx={{ m: 0, gap: 0.5 }}>
            {dynamicSuffix}
            {showClearButton && (
              <FilterButton
                onClick={() => {
                  if (multiple) {
                    onChange({ name, value: [] });
                  } else {
                    onChange({ name, value: null });
                  }
                }}
                iconName="close"
              />
            )}
          </InputAdornment>
        ) : undefined
      }
      disableClearable
      renderOption={(
        optionProps: HTMLAttributes<HTMLLIElement>,
        option: IChipLabeledOption<T>,
      ) => (
        <li {...optionProps} key={String(option.value)}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography variant="body2">{getOptionLabel(option)}</Typography>
            <AgencyChip agency={option.agency} />
          </Box>
        </li>
      )}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      options={cachedOptions}
      sx={(theme) => ({
        ["&.filled"]: {
          [` .separator`]: { display: "inline" },
          [`& .${inputAdornmentClasses.positionStart},
            & .${inputAdornmentClasses.positionStart} p, 
            & .${inputBaseClasses.input}:not(:focus)`]: {
            fontWeight: "bold",
            color: "grey.900",
          },
        },
        maxWidth: "300px",
        minWidth: "300px",
        [`& .${inputBaseClasses.root}`]: {
          paddingLeft: `${theme.spacing(3)}!important`,
        },
        [`& .${inputBaseClasses.input}`]: {
          paddingLeft: `${theme.spacing(1)}!important`,
        },
        [`&.${autocompleteClasses.root}.${autocompleteClasses.hasPopupIcon}`]: {
          [`.${outlinedInputClasses.root}`]: {
            paddingRight: theme.spacing(7.5),
          },
        },
        [`& .${autocompleteClasses.popupIndicator}`]: {
          paddingLeft: `${theme.spacing(1)}!important`,
        },
      })}
    />
  );
}

const MemoTableFilterAutocomplete = memo(TableFilterAutocomplete) as <T>(
  props: TableFilterAutocompleteProps<T>,
) => JSX.Element;

export default MemoTableFilterAutocomplete;
